import React from 'react'
import { useSelector } from 'react-redux'
import { useFieldArray } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import {
  useConfirmationModal,
  useControlledForm,
  useFormApiError,
  useFormReset,
  useLanguage,
} from 'hooks'
import { jobs } from 'orm'
import { mapTemplateToInternalState } from './actions'
import {
  TAB_NAMES,
  getActiveTab,
  getDefaultValues,
  getFinishedState,
  getInvalidState,
  getTabs,
} from './model'
import { getSchema } from './schema'
import { COMPETENCIES_FIELD } from './tabs/Competencies/model'
import {
  MODULES_FIELD,
  INTERVIEW_TEMPLATE_FIELD,
  INTERVIEW_MOUDLE_FIELD,
} from './tabs/Modules/model'
import {
  DEPARTMENT_FIELD,
  POSITION_FIELD,
  LANGUAGES_FIELD,
  AMOUNT_FIELD,
} from './tabs/Details/model'
import { MEMBERS_FIELD } from './tabs/Access/model'

const useJobPositionForm = (state, handlers) => {
  const { template, isOpen, isPositionManagement } = state
  const { onConfirm } = handlers

  const [apiError, setApiError] = React.useState(null)
  const [shouldReset, setShouldReset] = React.useState(false)
  const [showTemplates, setShowTemplates] = React.useState(false)

  const defaultValues = getDefaultValues(isPositionManagement)
  const schema = getSchema(isPositionManagement)

  const internalState = React.useMemo(
    () => mapTemplateToInternalState(template),
    [template],
  )

  const handleFormData = React.useCallback(
    async data => {
      await onConfirm(data, {
        onError: setApiError,
        onSuccess: () => setShouldReset(true),
      })
    },
    [onConfirm],
  )

  const {
    control,
    formState,
    handleFormApiError,
    handleFormReset,
    handleFormSubmit,
    setValue,
    watch,
    trigger,
    isDirtyOrInvalid,
  } = useControlledForm({
    values: template ? internalState : defaultValues,
    resolver: yupResolver(schema),
    handleFormData,
  })

  const competencies = useFieldArray({ control, name: COMPETENCIES_FIELD })
  const modules = useFieldArray({ control, name: MODULES_FIELD })

  useFormApiError({ apiError }, handleFormApiError)
  useFormReset(
    { shouldReset: shouldReset && formState.isSubmitSuccessful },
    handleFormReset,
  )

  React.useEffect(() => {
    if (isOpen) {
      setShouldReset(false)
    }
  }, [isOpen])

  React.useEffect(() => {
    if (template?.interview_template) {
      setShowTemplates(true)
    }
  }, [template?.interview_template])

  // reset department members access & templates when department has changed
  // user needs to set members access & templates all over
  React.useEffect(() => {
    if (formState.touchedFields[DEPARTMENT_FIELD]) {
      setValue(MEMBERS_FIELD, [], { shouldValidate: true })
      setValue(INTERVIEW_TEMPLATE_FIELD, null, { shouldValidate: false })
    }
  }, [
    control._formValues[DEPARTMENT_FIELD],
    formState.touchedFields[DEPARTMENT_FIELD],
  ])

  return {
    state: {
      control,
      fields: { competencies, modules },
      formState,
      isDirtyOrInvalid,
      shouldReset,
      isOpen,
      showTemplates,
    },
    handlers: {
      setShowTemplates,
      handleFormReset,
      handleFormSubmit,
      setApiError,
      setValue,
      watch,
      trigger,
    },
  }
}

const validatedFields = {
  [TAB_NAMES.DETAILS]: [
    POSITION_FIELD,
    DEPARTMENT_FIELD,
    LANGUAGES_FIELD,
    AMOUNT_FIELD,
  ],
  [TAB_NAMES.MODULES]: [INTERVIEW_MOUDLE_FIELD],
  [TAB_NAMES.COMPETENCIES]: [],
  [TAB_NAMES.PRIORITISED_OUTCOME]: [],
  [TAB_NAMES.ACCESS]: [],
}

const useStepsState = (state, handlers) => {
  const {
    form,
    language,
    projects,
    hasInverviewModuleDisabled,
    isEditMode,
    isOpen,
    isPositionManagement,
    showTemplates,
  } = state
  const {
    handleFormSubmit,
    handleFormReset,
    onBack,
    setShowTemplates,
    trigger,
  } = handlers

  const [activeTab, setActiveTab] = React.useState(TAB_NAMES.DETAILS)

  const tabs = React.useMemo(
    () =>
      getTabs(isPositionManagement).map(tab => {
        const isFinished = getFinishedState({ form, tabId: tab.id })
        const isInvalid = getInvalidState({ form, tabId: tab.id })

        return { ...tab, isFinished, isInvalid }
      }),
    [form],
  )

  const activeTabIndex = tabs.findIndex(({ id }) => id === activeTab)

  const tab = getActiveTab({
    form,
    language,
    // TODO: provide projects (categories list) via
    // separate API endpoint and build async searchable
    // combobox component
    projects: projects ?? [],
    tab: activeTab,
    hasInverviewModuleDisabled,
    isEditMode,
    isPositionManagement,
    showTemplates,
    setShowTemplates,
  })

  const isFirstStep = activeTabIndex === 0
  const isLastStep = activeTabIndex === tabs.length - 1

  const handleTab = React.useCallback(
    async id => {
      const isFormValid = await trigger(validatedFields[activeTab])

      if (isFormValid) {
        setActiveTab(id)
      }
    },
    [setActiveTab, activeTab, trigger],
  )

  React.useEffect(() => {
    if (form.formState.isSubmitted && !form.formState.isSubmitSuccessful) {
      const invalidTab = tabs.find(({ isInvalid }) => isInvalid === true)

      if (invalidTab) {
        handleTab(invalidTab.id)
      }
    }
  }, [form.formState.isSubmitting])

  const handleBack = React.useCallback(() => {
    if (isFirstStep && onBack) {
      handleFormReset()
      handleTab(TAB_NAMES.DETAILS)
      onBack()

      return
    }

    setActiveTab(tabs[activeTabIndex - 1].id)
  }, [activeTabIndex, tabs, handleFormReset, handleTab, onBack, isFirstStep])

  const handleNextStep = React.useCallback(async () => {
    if (isLastStep) {
      handleFormSubmit()

      return
    }

    const isFormValid = await trigger(validatedFields[activeTab])

    if (isFormValid) {
      setActiveTab(tabs[activeTabIndex + 1].id)
    }
  }, [activeTabIndex, tabs, handleFormSubmit, isLastStep, activeTab, trigger])

  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(TAB_NAMES.DETAILS)
    }
  }, [isOpen, setActiveTab])

  return {
    state: { activeTab, tab, tabs, isFirstStep, isLastStep },
    handlers: { handleBack, handleNextStep, handleTab },
  }
}

export const useJobPositionModal = (state, handlers) => {
  const data = useSelector(jobs.getModel)
  const language = useLanguage()

  const { template, isOpen, isPositionManagement } = state
  const { onBack, onCancel, onClose, onConfirm } = handlers

  const isEditMode = template !== null && template !== undefined

  const candidateStatuses = Object.keys(template?.candidates_status ?? {})
  const hasInterviewEligableCandidates = candidateStatuses.some(
    status => status === 'INTERVIEWED' || status === 'SCREENED',
  )
  const hasInterviewModule = template?.modules?.interview === true
  const hasInterviewTemplate =
    template?.interview_template === null ||
    template?.interview_template === undefined

  const hasInverviewModuleDisabled =
    hasInterviewEligableCandidates &&
    !hasInterviewTemplate &&
    hasInterviewModule

  const {
    state: {
      control,
      fields: { competencies, modules },
      formState,
      isDirtyOrInvalid,
      shouldReset,
      showTemplates,
    },
    handlers: {
      handleFormReset,
      handleFormSubmit,
      setShowTemplates,
      setValue,
      watch,
      trigger,
    },
  } = useJobPositionForm(
    { template, isOpen, isPositionManagement },
    { onConfirm },
  )

  const {
    state: { activeTab, tab, tabs, isFirstStep, isLastStep },
    handlers: { handleBack, handleNextStep, handleTab },
  } = useStepsState(
    {
      form: { control, competencies, formState, modules, setValue, watch },
      language,
      projects: data?.items,
      hasInverviewModuleDisabled,
      isEditMode,
      showTemplates,
      isOpen,
      isPositionManagement,
    },
    {
      handleFormSubmit,
      handleFormReset,
      onBack,
      trigger,
      setShowTemplates,
    },
  )

  const {
    handleConfirmationClose,
    handleConfirmationConfirm,
    handleConfirmationOpen,
    isConfirmationOpen,
  } = useConfirmationModal(() => {
    handleResetState()
    onClose()
  })

  const handleResetState = React.useCallback(() => {
    handleFormReset()
    handleTab(TAB_NAMES.DETAILS)
  }, [handleFormReset, handleTab])

  const handleCancel = React.useCallback(() => {
    if (isDirtyOrInvalid) {
      handleConfirmationOpen()

      return
    }

    handleResetState()
    onCancel()
  }, [handleConfirmationOpen, handleResetState, onCancel, isDirtyOrInvalid])

  const handleClose = React.useCallback(() => {
    if (isDirtyOrInvalid) {
      handleConfirmationOpen()

      return
    }

    handleResetState()
    onClose()
  }, [handleConfirmationOpen, handleResetState, onClose, isDirtyOrInvalid])

  React.useEffect(() => {
    if (shouldReset) {
      handleTab(TAB_NAMES.DETAILS)
    }
  }, [shouldReset])

  const handleConfirm = React.useCallback(handleNextStep, [handleNextStep])

  return {
    state: {
      activeTab,
      tab,
      tabs,
      isConfirmationOpen,
      isFirstStep,
      isLastStep,
      showTemplates,
    },
    handlers: {
      handleConfirmationClose,
      handleConfirmationConfirm,
      handleConfirmationOpen,
      handleBack,
      handleCancel,
      handleClose,
      handleConfirm,
      handleTab,
      setShowTemplates,
    },
  }
}
